// Lê o ano do Censo no navegador, antes do upload (PSV-363). Serve só à prévia
// e à confirmação do operador: o backend confere o ano de novo no arquivo.

export class CensusFileError extends Error {}

// O nome do CSV de dados é estável entre os anos do INEP; a pasta raiz do ZIP
// muda. A versão ("_V2") é opcional. O 2025 vem em tabelas; as que alimentam o
// banco são estas quatro.
const CENSUS_MEMBER =
  /(?:^|\/)(microdados_ed_basica|tabela_(escola|matricula|turma|docente))_(\d{4})(?:_v\d+)?\.csv$/i
const REQUIRED_TABLES = ['escola', 'matricula', 'turma', 'docente']

export type TCensusFileInfo = {
  year: number
  usedFiles: Array<string>
  ignoredFiles: Array<string>
}

const EOCD_SIGNATURE = 0x06054b50
const CENTRAL_ENTRY_SIGNATURE = 0x02014b50
const EOCD_SIZE = 22
const MAX_ZIP_COMMENT = 0xffff
const ZIP64_MARKER = 0xffffffff
const CSV_HEAD_BYTES = 64 * 1024
const LOCAL_HEADER_SIGNATURE = 0x04034b50
const READ_CHUNK_BYTES = 8 * 1024 * 1024
const NEWLINE = 10

const readSlice = (file: Blob, start: number, end: number) =>
  new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file.slice(start, end))
  })

// Latin-1: os nomes e o cabeçalho que importam aqui são ASCII.
const decodeAscii = (bytes: Uint8Array) =>
  Array.from(bytes, byte => String.fromCharCode(byte)).join('')

type TZipEntry = {
  name: string
  method: number
  compressedSize: number
  localHeaderOffset: number
}

/** Diretório central do ZIP, lido só do fim do arquivo. */
const readZipDirectory = async (file: Blob): Promise<Array<TZipEntry>> => {
  const tailStart = Math.max(0, file.size - EOCD_SIZE - MAX_ZIP_COMMENT)
  const tail = new DataView(await readSlice(file, tailStart, file.size))

  let eocd = -1
  for (let at = tail.byteLength - EOCD_SIZE; at >= 0; at -= 1) {
    if (tail.getUint32(at, true) === EOCD_SIGNATURE) {
      eocd = at
      break
    }
  }
  if (eocd < 0) {
    throw new CensusFileError('O arquivo não é um ZIP válido.')
  }

  const directorySize = tail.getUint32(eocd + 12, true)
  const directoryOffset = tail.getUint32(eocd + 16, true)
  if (directoryOffset === ZIP64_MARKER) {
    throw new CensusFileError(
      'ZIP em formato estendido (ZIP64) não é suportado. Envie o ZIP oficial do INEP.'
    )
  }

  const directory = new DataView(
    await readSlice(file, directoryOffset, directoryOffset + directorySize)
  )
  const entries: Array<TZipEntry> = []
  let at = 0
  while (
    at + 46 <= directory.byteLength &&
    directory.getUint32(at, true) === CENTRAL_ENTRY_SIGNATURE
  ) {
    const nameLength = directory.getUint16(at + 28, true)
    const extraLength = directory.getUint16(at + 30, true)
    const commentLength = directory.getUint16(at + 32, true)
    entries.push({
      name: decodeAscii(
        new Uint8Array(
          directory.buffer,
          directory.byteOffset + at + 46,
          nameLength
        )
      ),
      method: directory.getUint16(at + 10, true),
      compressedSize: directory.getUint32(at + 20, true),
      localHeaderOffset: directory.getUint32(at + 42, true),
    })
    at += 46 + nameLength + extraLength + commentLength
  }
  return entries
}

/** Nomes dos arquivos do ZIP, lidos só do diretório central, no fim dele. */
export const listZipEntries = async (file: Blob): Promise<Array<string>> =>
  (await readZipDirectory(file)).map(entry => entry.name)

const baseName = (name: string) => name.split('/').pop() ?? name

const zipInfo = async (file: File): Promise<TCensusFileInfo> => {
  const usedFiles: Array<string> = []
  const ignoredFiles: Array<string> = []
  const years = new Set<number>()
  const tables = new Set<string>()
  let hasSingleFile = false

  ;(await listZipEntries(file)).forEach(name => {
    if (name.startsWith('__MACOSX/') || name.endsWith('/')) return
    const match = CENSUS_MEMBER.exec(name)
    if (match) {
      usedFiles.push(baseName(name))
      years.add(Number(match[3]))
      if (match[2]) tables.add(match[2].toLowerCase())
      else hasSingleFile = true
    } else if (/\.csv$/i.test(name)) {
      ignoredFiles.push(baseName(name))
    }
  })

  if (years.size === 0) {
    throw new CensusFileError(
      'O ZIP não traz os microdados do Censo (microdados_ed_basica_AAAA.csv ou Tabela_Escola_AAAA.csv). Envie o ZIP oficial do INEP.'
    )
  }
  if (years.size > 1) {
    throw new CensusFileError(
      `O ZIP traz mais de um ano do Censo (${Array.from(years)
        .sort()
        .join(', ')}). Envie o ZIP de um único ano.`
    )
  }
  if (hasSingleFile && tables.size > 0) {
    throw new CensusFileError(
      'O ZIP traz mais de um conjunto de microdados do Censo. Envie o ZIP de um único ano.'
    )
  }
  const missing = REQUIRED_TABLES.filter(table => !tables.has(table))
  if (!hasSingleFile && missing.length > 0) {
    throw new CensusFileError(
      `O ZIP não traz todas as tabelas do Censo: faltam ${missing
        .map(table => `Tabela_${table[0].toUpperCase()}${table.slice(1)}`)
        .join(', ')}. Envie o ZIP oficial do INEP completo.`
    )
  }
  return { year: Array.from(years)[0], usedFiles, ignoredFiles }
}

const yearFromCsv = async (file: File) => {
  const text = decodeAscii(
    new Uint8Array(await readSlice(file, 0, CSV_HEAD_BYTES))
  )
  const [header = '', firstRow = ''] = text.split(/\r?\n/)
  // BOM do UTF-8 lido como latin-1.
  const columns = header.replace(/^\u00ef\u00bb\u00bf/, '').split(';')
  const index = columns.indexOf('NU_ANO_CENSO')
  const value = firstRow.split(';')[index] ?? ''

  if (index < 0 || !/^\d{4}$/.test(value.trim())) {
    throw new CensusFileError(
      'Não foi possível ler o ano do Censo (coluna NU_ANO_CENSO) na primeira linha do arquivo.'
    )
  }
  return Number(value)
}

/**
 * Ano do Censo e arquivos que serão importados: CSV pela 1ª linha, ZIP pelos
 * nomes do índice (sem ler o arquivo inteiro).
 */
export const readCensusFile = async (file: File): Promise<TCensusFileInfo> =>
  /\.zip$/i.test(file.name)
    ? zipInfo(file)
    : {
        year: await yearFromCsv(file),
        usedFiles: [file.name],
        ignoredFiles: [],
      }

// Nativo nos navegadores atuais, mas ainda sem tipo no lib.dom do TypeScript
// 4.9 usado pelo projeto.
declare const DecompressionStream: {
  new (format: 'deflate-raw'): TransformStream<Uint8Array, Uint8Array>
}

type TLineCount = { newlines: number; lastByte: number }

const countNewlines = (bytes: Uint8Array, count: TLineCount) => {
  for (let index = 0; index < bytes.length; index += 1) {
    if (bytes[index] === NEWLINE) count.newlines += 1
  }
  if (bytes.length > 0) count.lastByte = bytes[bytes.length - 1]
}

async function* readChunks(file: Blob, start: number, end: number) {
  for (let at = start; at < end; at += READ_CHUNK_BYTES) {
    yield new Uint8Array(
      await readSlice(file, at, Math.min(at + READ_CHUNK_BYTES, end))
    )
  }
}

const countStored = async (file: Blob, start: number, end: number) => {
  const count: TLineCount = { newlines: 0, lastByte: NEWLINE }
  for await (const chunk of readChunks(file, start, end)) {
    countNewlines(chunk, count)
  }
  return count
}

// Descompressão nativa do navegador; lê os blocos com FileReader para não
// depender de Blob.stream().
const countDeflated = async (file: Blob, start: number, end: number) => {
  const count: TLineCount = { newlines: 0, lastByte: NEWLINE }
  const stream = new DecompressionStream('deflate-raw')
  const writer = stream.writable.getWriter()
  const reader = stream.readable.getReader()
  const writing = (async () => {
    for await (const chunk of readChunks(file, start, end)) {
      await writer.write(chunk)
    }
    await writer.close()
  })()
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    countNewlines(value, count)
  }
  await writing
  return count
}

// Linhas de dados: quebras de linha, menos o cabeçalho, mais a última linha
// quando o arquivo não termina em quebra.
const dataRows = ({ newlines, lastByte }: TLineCount) =>
  Math.max(0, newlines - 1 + (lastByte === NEWLINE ? 0 : 1))

/**
 * Escolas do arquivo: as linhas do CSV ou, no ZIP, do microdados_ed_basica ou
 * da Tabela_Escola. Lê o arquivo inteiro em blocos, sem enviar nada.
 */
export const countCensusSchools = async (file: File): Promise<number> => {
  if (!/\.zip$/i.test(file.name)) {
    return dataRows(await countStored(file, 0, file.size))
  }

  const entry = (await readZipDirectory(file)).find(item => {
    const match = CENSUS_MEMBER.exec(item.name)
    return (
      !item.name.startsWith('__MACOSX/') &&
      !!match &&
      (!match[2] || match[2].toLowerCase() === 'escola')
    )
  })
  if (!entry) {
    throw new CensusFileError('O ZIP não traz os microdados do Censo.')
  }

  const header = new DataView(
    await readSlice(file, entry.localHeaderOffset, entry.localHeaderOffset + 30)
  )
  if (header.getUint32(0, true) !== LOCAL_HEADER_SIGNATURE) {
    throw new CensusFileError('O ZIP está corrompido.')
  }
  const start =
    entry.localHeaderOffset +
    30 +
    header.getUint16(26, true) +
    header.getUint16(28, true)
  const end = start + entry.compressedSize

  if (entry.method === 0) return dataRows(await countStored(file, start, end))
  if (entry.method === 8) return dataRows(await countDeflated(file, start, end))
  throw new CensusFileError(
    'O ZIP usa um método de compressão que o navegador não lê. Envie o ZIP oficial do INEP.'
  )
}
